import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Search, Trash2, Copy, FileText, Sparkles } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { inputCls } from "./AFields";
import AIImageStudio from "./AIImageStudio";
import { Modal } from "./Participants";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get(`/admin/media/list?q=${encodeURIComponent(q)}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadFiles = async (files) => {
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/upload", fd);
      } catch (e) { toast.error(errMsg(e)); }
    }
    toast.success("Upload complete");
    load();
  };

  const isImg = (m) => /\.(webp|jpe?g|png)$/i.test(m.url);

  return (
    <div data-testid="admin-media">
      <SEO title="Media Library" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Media Library</h1>
      <p className="mt-1 text-sm text-slate-500">Centralized uploads and reviewed AI-generated media (JPG, PNG, WEBP, PDF — max 10MB).</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => ref.current?.click()} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="media-upload-btn"><Upload size={15} /> Upload Files</button>
        <button onClick={() => setAiOpen(true)} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="media-ai-generate-btn"><Sparkles size={15} /> Generate with AI</button>
        <input ref={ref} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
          onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ""; }} data-testid="media-file-input" />
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search media…" className={`${inputCls} !w-64 !pl-10`} data-testid="media-search" />
        </div>
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="media-search-btn">Search</button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles([...e.dataTransfer.files]); }}
        className={`mt-5 rounded-lg border-2 border-dashed p-6 text-center text-sm font-semibold transition-colors ${dragging ? "border-saffron-500 bg-saffron-100/50 text-navy" : "border-gray-300 bg-white text-slate-500"}`}
        data-testid="media-dropzone">
        Drag & drop files here to upload
      </div>

      <AIImageStudio open={aiOpen} onClose={() => setAiOpen(false)} onUse={() => load()} entity="media" saveLabel="Save to Media Library" />

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((m) => (
          <button key={m.id} onClick={() => setOpen({ ...m })} className="group rounded-lg border border-gray-200 bg-white p-3 text-left transition-shadow hover:shadow-md" data-testid={`media-item-${m.id}`}>
            {isImg(m)
              ? <img src={m.thumb || m.url} alt={m.alt_text || m.title} loading="lazy" className="aspect-square w-full rounded-md object-cover" />
              : <span className="flex aspect-square w-full items-center justify-center rounded-md bg-navy-50"><FileText size={32} className="text-navy" strokeWidth={1.4} /></span>}
            <p className="mt-2 truncate text-xs font-semibold text-navy">{m.title}</p>
          </button>
        ))}
        {items.length === 0 && <p className="col-span-full py-10 text-center text-sm text-slate-400">No media uploaded yet.</p>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(null)} title="Media Details" testid="media-detail">
          {isImg(open) && <img src={open.url} alt={open.alt_text || open.title} className="max-h-64 w-full rounded-lg object-contain" />}
          <div className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Display Title</label>
              <div className="flex gap-2">
                <input value={open.title} onChange={(e) => setOpen({ ...open, title: e.target.value })} className={inputCls} data-testid="media-title" />
                <button onClick={async () => { await api.put(`/admin/media/${open.id}`, { title: open.title, alt_text: open.alt_text || "", caption: open.caption || "", description: open.description || "" }); toast.success("Saved"); load(); }}
                  className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="media-rename">Save</button>
              </div>
            </div>
            {["alt_text", "caption", "description"].map((key) => (
              <div key={key}>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{key.replace("_", " ")}</label>
                <textarea rows={key === "description" ? 3 : 2} value={open[key] || ""} onChange={(e) => setOpen({ ...open, [key]: e.target.value })} className={inputCls} data-testid={`media-${key}`} />
              </div>
            ))}
            <div className="flex items-center gap-2 rounded-md bg-navy-50/60 px-3 py-2">
              <code className="flex-1 truncate text-xs text-slate-600">{open.url}</code>
              <button onClick={() => { navigator.clipboard.writeText(open.url); toast.success("URL copied"); }} className="text-navy" aria-label="Copy URL" data-testid="media-copy"><Copy size={15} /></button>
            </div>
            <div className="flex justify-between">
              <button onClick={async () => { if (window.confirm("Are you sure you want to delete this file?")) { await api.delete(`/admin/media/${open.id}`); setOpen(null); load(); toast.success("Deleted"); } }}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-4 py-2 text-xs font-bold text-red-500" data-testid="media-delete"><Trash2 size={13} /> Delete</button>
              <button onClick={() => setOpen(null)} className="rounded-md border border-gray-200 px-4 py-2 text-xs font-bold text-navy">Close</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
